import type React from 'react'

/**
 * Props that turn a plain `div`/`span` into an activatable region: click plus the keyboard equivalent
 * (Enter/Space), with the button role and tab stop. Returns nothing when `enabled` is false, so a
 * read-only region stays inert and un-focusable.
 *
 * Every editable region in the document header hand-rolled these four props, which meant some columns
 * ended up focusable but not keyboard-activatable. Going through one helper keeps them consistent.
 */
export function activatable(
  enabled: boolean,
  activate: (element: HTMLElement) => void,
  /** For regions nested inside another activatable region: don't also trigger the parent's editor. */
  opts?: { stopPropagation?: boolean }
) {
  if (!enabled) return {}
  return {
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      if (opts?.stopPropagation) e.stopPropagation()
      activate(e.currentTarget)
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (opts?.stopPropagation) e.stopPropagation()
        activate(e.currentTarget)
      }
    },
    role: 'button',
    tabIndex: 0
  }
}

export default activatable
